<!--
    Component: Entity Selector
    Purpose:   Select any MDM Table, assisted by search filter and Module/Folder selection.
-->
<template>
  <div ref="entitySelector" class="entitySelector">
    <input type="hidden" v-model="entityGuid" ref="entityGuid" />
    <div class="custom-select selector-control" tabindex="10" @click="onEntityCaptionClick">
      <div class="selector-caption">
        {{ selectedEntityName }}
      </div>
    </div>
    <div class="selector-dropdown shadow-lg" v-show="showDropdown">
      <div class="search__wrapper">
        <b-input type="search" v-model="entitySearchValue" placeholder="Search" debounce="300" />
      </div>
      <b-list-group class="group__item selector-list">
        <b-list-group-item v-for="entityItem in entitiesToRender" :key="entityItem.entityGuid" class="selector-item "
          :active="selectedEntity === entityItem" @click="setSelectedEntity(entityItem)">
          <div>
            <font-awesome-icon :icon="['fas', entityItem.icon || 'table']" />&nbsp;
            <strong>{{ entityItem.displayName }}</strong>
          </div>
          <div class="component-name">{{ getModuleNameForEntity(entityItem) }}</div>
          <div class="component-name">{{ getFolderNameForEntity(entityItem) }}</div>
        </b-list-group-item>
      </b-list-group>
    </div>
  </div>
</template>

<script>

export default {

  name: 'EntitySelector',

  inject: ['entityUtils', 'apiUtils'],

  props: [
    /** Selected Table Guid */
    'value',
    /** Array of Entity guids to exclude. */
    'entityGuidsToIgnore',
  ],

  data() {
    return {
      control: {
        componentsLoaded: false,
        droppedDown: false,
      },
      // Components.
      modules: [],
      folders: [],
      entities: [],
      /** Partial Entity name to filter list. */
      entitySearchValue: '',
      /** Entity {id,text} selected in list. */
      selectedEntity: null,
    };
  },


  watch: {

    // Entity Guid.
    entityGuid: {
      handler(entityGuid) {
        this.setSelectedEntityByGuid(entityGuid);
      },
      immediate: true,
    },

  },

  computed: {

    /** Selected Entity Guid. */
    entityGuid: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },

    showDropdown() {
      return this.control.componentsLoaded && this.control.droppedDown;
    },

    /** Filtered list of Fields to display in list. */
    entitiesToRender() {
      return this.entities
        /** Exclude Entities to be ignored. */
        .filter(ent => !(this.entityGuidsToIgnore || []).length || !this.entityGuidsToIgnore.find(entIgnore => entIgnore.entityGuid == ent.entityGuid))
        /** Exclude entities that don't match search value. */
        .filter(ent => this.entityMatchesSearch(ent));
    },

    selectedEntityGuid() {
      return this.selectedEntity?.entityGuid;
    },

    selectedEntityName() {
      return this.selectedEntity?.displayName;
    },

  },

  methods: {

    /** Populate component hierarchy to list tables with qualified paths (module/folder). */
    async initialise() {
      const loadModules = async function (comp) {
        comp.modules = (await comp.apiUtils.getAllModules() || []).filter(mod => !mod.isSystem);
      };
      const loadFolders = async function (comp) {
        comp.folders = (await comp.apiUtils.getAllFolders() || []);
      };
      const loadEntites = async function (comp) {
        comp.entities = (await comp.apiUtils.getAllEntities() || []);
      };
      await Promise.all([loadModules(this), loadFolders(this), loadEntites(this)]);
      this.control.componentsLoaded = (this.modules.length && this.folders.length && this.entities.length);
      this.setSelectedEntityByGuid(this.value);
    },

    /** Intercepts all click events and closes the dropdown if clicked outside. */
    handleClickOutside(event) {
      if (this.$refs.entitySelector && !this.$refs.entitySelector.contains(event.target)) {
        // Click event outside this component.
        this.control.droppedDown = false;
      }
    },

    // Module.
    getModuleForEntity(entityItem) {
      return this.modules.find(mod => mod.moduleGuid == entityItem.moduleGuid);
    },
    getModuleNameForEntity(entityItem) {
      return this.getModuleForEntity(entityItem)?.moduleName || '';
    },

    // Folder.
    getFolderForEntity(entityItem) {
      return this.folders.find(fld => fld.folderGuid == entityItem.folderGuid);
    },
    getFolderNameForEntity(entityItem) {
      return this.getFolderForEntity(entityItem)?.folderName || '';
    },

    onEntityCaptionClick() {
      if (!this.control.componentsLoaded) return;
      this.control.droppedDown = !this.control.droppedDown;
    },

    entityMatchesSearch(entity) {
      if (!this.entitySearchValue.length) return true;
      const moduleName = this.getModuleNameForEntity(entity);
      const folderName = this.getFolderNameForEntity(entity);
      for (const searchWord of this.entitySearchValue.split(/[^A-Z0-9]+/ig)) {
        const reSearchWord = new RegExp(`${RegExp.escape(searchWord)}`, 'ig');
        if (!reSearchWord.test(moduleName) && !reSearchWord.test(folderName) && !reSearchWord.test(entity.displayName))
          return false;
      }
      return true;
    },

    // Selected Entity.

    setSelectedEntityByGuid(entityGuid) {
      if (!this.control.componentsLoaded) return;
      if (this.selectedEntityGuid == entityGuid) return;
      this.setSelectedEntity(this.entities.find(ent => ent.entityGuid == entityGuid));
    },

    setSelectedEntity(entity) {
      this.selectedEntity = entity;
      this.entityGuid = this.selectedEntityGuid;
      if (!!this.selectedEntity) {
        this.control.droppedDown = false;
        this.entitySearchValue = '';
      }
    },

  },

  /** Called after the instance has finished processing all state-related options. */
  async created() {
  },

  /** Called right before the component is to be mounted. */
  async beforeMount() {
    this.initialise();
  },

  /** Called after the component has been mounted. */
  async mounted() {
    // Monitor for clicks outside component to close 'dropdown'.
    document.addEventListener('click', this.handleClickOutside);
  },

  beforeDestroy() {
    // Remove click event listener.
    document.removeEventListener('click', this.handleClickOutside);
  },

};

</script>

<style lang="scss" scoped>
.entitySelector {
  position: relative;
}

.selector-caption {
  margin-right: 20px;
  overflow: hidden;
  white-space: nowrap;
}

.selector-dropdown {
  position: absolute;
  width: 100%;
  z-index: 1000;
  background-color: #fff;
  border: 1px solid #ccc;
  border-radius: 6px;
  box-shadow: 0 12px 16px rgba(0, 0, 0, 0.5) !important;

  .selector-list {

    min-height: 160px;
    max-height: 320px;
    padding: 2px;
    border: 1px solid #ccc;
    border-top-right-radius: 6px;
    border-top-left-radius: 6px;
    overflow-y: auto;

    .selector-item {
      padding: 5px;

      &:not(.active):hover {
        background-color: #ffffe0;
      }

      .component-name {
        font-size: .9em;
      }
    }
  }

}
</style>